import { motion } from 'framer-motion';
import BrandMark from '../components/BrandMark.jsx';
import EmailCapture from '../components/EmailCapture.jsx';
import HeroAnimation from '../components/HeroAnimation.jsx';

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay },
  }),
};

export default function ComingSoon() {
  return (
    <div className="relative min-h-svh-safe overflow-hidden safe-area-pb">
      {/* Ambient background blooms */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -right-24 h-[320px] w-[320px] rounded-full opacity-60 blur-3xl sm:-top-40 sm:-right-40 sm:h-[520px] sm:w-[520px]"
        style={{
          background:
            'radial-gradient(circle, rgba(244,180,0,0.18) 0%, rgba(244,180,0,0) 70%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 -left-24 h-[320px] w-[320px] rounded-full opacity-50 blur-3xl sm:-bottom-48 sm:-left-32 sm:h-[480px] sm:w-[480px]"
        style={{
          background:
            'radial-gradient(circle, rgba(11,18,32,0.10) 0%, rgba(11,18,32,0) 70%)',
        }}
      />

      {/* Top bar */}
      <header className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between gap-3 px-5 pt-5 sm:px-10 sm:pt-7">
        <motion.div initial="hidden" animate="show" variants={fadeUp} custom={0}>
          <BrandMark />
        </motion.div>
        <motion.div
          initial="hidden"
          animate="show"
          variants={fadeUp}
          custom={0.1}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-saffron-gold/30 bg-saffron-gold/10 px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-ink sm:gap-2 sm:px-3 sm:text-xs sm:tracking-normal sm:normal-case"
        >
          <span className="h-1.5 w-1.5 animate-saffron-breathe rounded-full bg-saffron-gold" />
          Launching 2026
        </motion.div>
      </header>

      {/* Hero */}
      <main className="relative z-10 mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-10 px-5 pb-16 pt-10 sm:gap-12 sm:px-10 sm:pb-24 sm:pt-20 lg:grid-cols-[1.1fr_1fr] lg:gap-16 lg:pt-24">
        <section className="flex flex-col items-start">
          <motion.span
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={0.15}
            className="mb-4 inline-flex items-center gap-2 rounded-full border border-soft-border bg-pure-surface/70 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.14em] text-slate-text backdrop-blur sm:mb-5 sm:px-3 sm:text-[11px]"
          >
            Intelligence-first home search
          </motion.span>

          <motion.h1
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={0.2}
            className="font-display text-[2rem] font-semibold leading-[1.08] tracking-tight text-ink text-balance sm:text-5xl sm:leading-[1.05] lg:text-[3.6rem]"
          >
            <span className="font-normal">See what others miss.</span>
            <br />
            <span className="text-saffron-gold">
              Decide what others can't.
            </span>
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={0.32}
            className="mt-4 max-w-lg text-[15px] leading-relaxed text-slate-text sm:mt-5 sm:text-lg"
          >
            <span className="block">
              Real insights for any location. Flood risk, school catchments,
              commute, future development.
            </span>
            <span className="mt-4 block sm:mt-6">
              All at every place, before you sign anything.
            </span>
          </motion.p>

          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={0.45}
            className="mt-6 w-full sm:mt-8"
          >
            <EmailCapture />
          </motion.div>

          {/* Cities — desktop: under the form. Mobile: hidden here, rendered below the globe. */}
          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={0.6}
            className="mt-8 hidden flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-text lg:flex"
          >
            <span className="flex items-center gap-1.5">
              <span className="h-1 w-1 rounded-full bg-ink/30" /> Starting in Chennai
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-1 w-1 rounded-full bg-ink/30" /> More cities to follow
            </span>
          </motion.div>
        </section>

        <motion.section
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
          className="flex w-full flex-col items-center gap-5 lg:items-end lg:gap-0 lg:justify-end"
        >
          <HeroAnimation />

          {/* Cities — mobile/tablet only, sits beneath the globe */}
          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={0.6}
            className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] text-slate-text lg:hidden"
          >
            <span className="flex items-center gap-1.5">
              <span className="h-1 w-1 rounded-full bg-ink/30" /> Starting in Chennai
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-1 w-1 rounded-full bg-ink/30" /> More cities to follow
            </span>
          </motion.div>
        </motion.section>
      </main>

      {/* Footer */}
      <footer className="relative z-10 mx-auto w-full max-w-6xl px-5 pb-7 sm:px-10 sm:pb-8">
        <div className="flex flex-col items-center gap-4 border-t border-soft-border pt-6 text-center text-[11px] text-slate-text sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:text-left sm:text-xs">
          <p className="leading-relaxed">
            © {new Date().getFullYear()} Myly.{' '}
            <span className="text-ink/60">Beyond Brochures.</span>
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 sm:justify-end sm:gap-6">
            <a
              href="mailto:admin@myly.io"
              className="transition-colors hover:text-ink"
            >
              admin@myly.io
            </a>
            <span className="h-3 w-px bg-soft-border sm:hidden" aria-hidden />
            <a href="#" className="transition-colors hover:text-ink">
              Privacy
            </a>
            <span className="h-3 w-px bg-soft-border sm:hidden" aria-hidden />
            <a href="#" className="transition-colors hover:text-ink">
              Terms
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
